// src/Components/UserActivities/ActivityTables.jsx

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ChartUserDistribution from './ChartUserDistribution.jsx';

const ACTIVITY_KEYS = [
  'Upload',
  'Download',
  'ViewData',
  'GenerateDocument',
  'ImportData',
  'ExportData',
  'CreateEntities',
  'DeleteData',
  'EditData',
];

/**
 * Build per-period, per-user aggregates from userTotals:
 * { [dateString]: Array<{ userId, counts, totalExcludingOther }> }
 */
function buildClientStats(userTotalsByDay, periodFn) {
  const stats = {};

  Object.entries(userTotalsByDay).forEach(([date, entries]) => {
    const period = periodFn(date);
    stats[period] ??= {};

    entries.forEach(({ userId, counts }) => {
      if (!userId) return;
      const row = stats[period][userId] ??= { id: userId };
      ACTIVITY_KEYS.forEach((k) => {
        row[k] = (row[k] || 0) + (counts[k] || 0);
      });
    });
  });

  // compute total for each row
  Object.values(stats).forEach((clientMap) =>
    Object.values(clientMap).forEach((r) => {
      r.total = ACTIVITY_KEYS.reduce((sum, k) => sum + (r[k] || 0), 0);
    })
  );

  return stats;
}

export default function ActivityTables({
  userTotals,
  subAccountants,
  clients,
  direction,
}) {
  const { t } = useTranslation();

  const users = useMemo(
    () => [...subAccountants, ...clients],
    [subAccountants, clients]
  );

  const allStats = useMemo(
    () => buildClientStats(userTotals, () => 'overall'),
    [userTotals]
  );

  const monthStats = useMemo(
    () => buildClientStats(userTotals, (d) => dayjs(d).format('MMMM YYYY')),
    [userTotals]
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('Client'),
      },
      ...ACTIVITY_KEYS.map((k) => ({
        field: k,
        headerName: t(k),
      })),
    ],
    [t]
  );

  // helper to render a table given an array of row objects
  const renderTable = (rows) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell align="center">{t('Client')}</TableCell>
          {ACTIVITY_KEYS.map((key) => (
            <TableCell key={key} align="center">
              {t(key)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell align="center">
              {users.find((u) => u.value === row.id)?.label || row.id}
            </TableCell>
            {ACTIVITY_KEYS.map((key) => (
              <TableCell key={key} align="center">
                {row[key]?.toLocaleString?.() ?? row[key] ?? 0}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box dir={direction} sx={{ px: 2, mt: 2 }}>
      {/* Overall breakdown + chart */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 4,
        }}
      >
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t('Total')}
            </Typography>
            <Typography variant="h4" mb={2}>
              {Object.values(allStats.overall || {})
                .reduce((sum, r) => sum + r.total, 0)
                .toLocaleString()}
            </Typography>
            {renderTable(Object.values(allStats.overall || {}))}
          </CardContent>
        </Card>
        <ChartUserDistribution
          userTotals={Object.values(allStats.overall).map((r) => ({
            id: r.id,
            total: r.total,
          }))}
          users={users}
          t={t}
        />
      </Box>

      {/* Monthly breakdown */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        {Object.entries(monthStats).map(([period, clientMap]) => (
          <Card key={period} variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {period}
              </Typography>
              {renderTable(Object.values(clientMap))}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

ActivityTables.propTypes = {
  userTotals: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string.isRequired,
        counts: PropTypes.object.isRequired,
        totalExcludingOther: PropTypes.number.isRequired,
      })
    )
  ).isRequired,
  subAccountants: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  direction: PropTypes.oneOf(['ltr', 'rtl']).isRequired,
};
